import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Notify } from '../Utility/Notify';
import { AssignleadsinteamleaderAction } from '../Redux/Actions/UsersAction';
import { Get_All_Sales_By_TeamLeader_Email_Action } from '../Redux/Actions/SalesAction';
import { GetAll_stage_Data } from '../Redux/Actions/Stage_Action';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const chunkArray = (array, size) => {
  const chunked = [];
  for (let i = 0; i < array.length; i += size) chunked.push(array.slice(i, i + size));
  return chunked;
};

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const avatarBg = (name = '') => {
  const colors = ['#C7D2FE','#FDE68A','#A7F3D0','#FBCFE8','#BAE6FD','#DDD6FE','#FED7AA','#D1FAE5','#E0E7FF','#FCE7F3'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[h];
};
const avatarFg = (name = '') => {
  const colors = ['#4338CA','#92400E','#065F46','#9D174D','#075985','#5B21B6','#92400E','#064E3B','#312E81','#831843'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % colors.length;
  return colors[h];
};

/* ─── CSS — identical tokens to AssignLeadsModal ─────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  @keyframes tla-in  { from{opacity:0;transform:translateY(20px) scale(0.97)} to{opacity:1;transform:none} }
  @keyframes tla-spin { to{transform:rotate(360deg)} }

  .tla-overlay {
    position:fixed; inset:0; z-index:99999;
    display:flex; align-items:center; justify-content:center;
    background:rgba(15,20,40,0.55); backdrop-filter:blur(6px);
    padding:16px; font-family:'DM Sans',sans-serif;
  }
  .tla-modal {
    background:#fff; border-radius:20px;
    width:100%; max-width:640px; max-height:90vh;
    overflow:hidden; display:flex; flex-direction:column;
    box-shadow:0 24px 64px rgba(0,0,0,0.18);
    animation:tla-in 0.28s cubic-bezier(0.34,1.2,0.64,1) both;
  }

  /* header */
  .tla-header { padding:24px 28px 16px; border-bottom:1px solid #F1F3F9; flex-shrink:0; }
  .tla-header-top { display:flex; align-items:flex-start; justify-content:space-between; }
  .tla-title { font-size:22px; font-weight:700; color:#0F172A; margin:0 0 6px; letter-spacing:-0.3px; }
  .tla-subtitle { display:flex; align-items:center; gap:6px; font-size:13px; color:#64748B; font-weight:500; }
  .tla-subtitle-dot { width:8px; height:8px; border-radius:50%; background:#1E40AF; flex-shrink:0; }
  .tla-close {
    width:34px; height:34px; border-radius:50%;
    border:1.5px solid #E2E8F0; background:#F8FAFC;
    display:flex; align-items:center; justify-content:center;
    cursor:pointer; color:#64748B; flex-shrink:0; transition:all 0.15s;
  }
  .tla-close:hover { background:#F1F5F9; color:#0F172A; border-color:#CBD5E1; }

  /* body */
  .tla-body {
    flex:1; overflow-y:auto; padding:24px 28px;
    scrollbar-width:thin; scrollbar-color:#CBD5E1 transparent;
  }
  .tla-body::-webkit-scrollbar { width:5px; }
  .tla-body::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:999px; }

  /* section label */
  .tla-section-label {
    font-size:11px; font-weight:700; letter-spacing:0.1em;
    color:#94A3B8; text-transform:uppercase; margin:0 0 14px;
  }

  /* summary banner */
  .tla-summary {
    display:flex; align-items:center; gap:16px;
    background:#EFF6FF; border:1px solid #BFDBFE;
    border-radius:14px; padding:18px 20px; margin-bottom:28px;
  }
  .tla-summary-icon {
    width:44px; height:44px; border-radius:12px;
    background:#DBEAFE; display:flex; align-items:center; justify-content:center; flex-shrink:0;
  }
  .tla-summary-count { font-size:26px; font-weight:700; color:#1E40AF; line-height:1; }
  .tla-summary-label { font-size:12px; color:#64748B; margin-top:2px; }
  .tla-summary-from { font-size:13px; color:#0F172A; font-weight:600; }
  .tla-summary-from-label { font-size:11px; color:#94A3B8; }

  /* search */
  .tla-search-wrap { position:relative; margin-bottom:12px; }
  .tla-search-input {
    width:100%; padding:14px 44px 14px 44px;
    border:1.5px solid #E2E8F0; border-radius:12px;
    font-family:'DM Sans',sans-serif; font-size:14px; color:#0F172A;
    background:#F8FAFC; outline:none; transition:border-color 0.15s; box-sizing:border-box;
  }
  .tla-search-input:focus { border-color:#1E40AF; background:#fff; }
  .tla-search-input::placeholder { color:#94A3B8; }
  .tla-search-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:#94A3B8; pointer-events:none; }
  .tla-search-arrow { position:absolute; right:14px; top:50%; transform:translateY(-50%); color:#94A3B8; pointer-events:none; }

  /* sales list */
  .tla-sales-list {
    border:1.5px solid #E2E8F0; border-radius:12px;
    overflow:hidden; margin-bottom:28px; max-height:220px; overflow-y:auto;
    scrollbar-width:thin; scrollbar-color:#CBD5E1 transparent;
  }
  .tla-sales-list::-webkit-scrollbar { width:4px; }
  .tla-sales-list::-webkit-scrollbar-thumb { background:#CBD5E1; border-radius:4px; }
  .tla-sales-item {
    display:flex; align-items:center; gap:12px;
    padding:14px 16px; cursor:pointer; transition:background 0.12s;
    border-bottom:1px solid #F1F3F9;
  }
  .tla-sales-item:last-child { border-bottom:none; }
  .tla-sales-item:hover { background:#F8FAFC; }
  .tla-sales-item.selected { background:#EFF6FF; }
  .tla-avatar {
    width:38px; height:38px; border-radius:50%; flex-shrink:0;
    display:flex; align-items:center; justify-content:center;
    font-size:13px; font-weight:700; letter-spacing:0.5px;
  }
  .tla-sales-name { font-size:14px; font-weight:600; color:#0F172A; }
  .tla-sales-email { font-size:12px; color:#94A3B8; font-weight:500; }
  .tla-radio {
    width:20px; height:20px; border-radius:50%; border:2px solid #CBD5E1;
    flex-shrink:0; display:flex; align-items:center; justify-content:center; transition:all 0.15s;
  }
  .tla-radio.checked { border-color:#1E3A8A; background:#1E3A8A; }
  .tla-radio.checked::after { content:''; width:8px; height:8px; border-radius:50%; background:#fff; }

  /* bottom two-col */
  .tla-bottom-grid { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin-bottom:8px; }

  /* stage options */
  .tla-stage-option {
    display:flex; align-items:flex-start; gap:10px;
    padding:10px 12px; border-radius:10px; cursor:pointer;
    transition:background 0.12s; margin-bottom:6px;
  }
  .tla-stage-option:hover { background:#F8FAFC; }
  .tla-stage-radio {
    width:20px; height:20px; border-radius:50%; border:2px solid #CBD5E1;
    flex-shrink:0; display:flex; align-items:center; justify-content:center;
    margin-top:1px; transition:all 0.15s;
  }
  .tla-stage-radio.checked { border-color:#1E3A8A; background:#1E3A8A; }
  .tla-stage-radio.checked::after { content:''; width:8px; height:8px; border-radius:50%; background:#fff; }
  .tla-stage-text-name { font-size:14px; font-weight:600; color:#0F172A; }
  .tla-stage-text-desc { font-size:12px; color:#94A3B8; margin-top:1px; }
  .tla-stage-select {
    width:100%; padding:10px 12px; border:1.5px solid #E2E8F0; border-radius:10px;
    font-family:'DM Sans',sans-serif; font-size:13px; color:#0F172A;
    background:#F8FAFC; outline:none; margin-top:8px; cursor:pointer; transition:border-color 0.15s;
  }
  .tla-stage-select:focus { border-color:#1E40AF; }

  /* advanced toggles */
  .tla-toggle-row {
    display:flex; align-items:flex-start; gap:10px;
    padding:10px 12px; border-radius:10px; cursor:pointer;
    transition:background 0.12s; margin-bottom:6px;
  }
  .tla-toggle-row:hover { background:#F8FAFC; }
  .tla-checkbox {
    width:20px; height:20px; border-radius:6px; border:2px solid #CBD5E1;
    flex-shrink:0; margin-top:1px; display:flex; align-items:center; justify-content:center; transition:all 0.15s;
  }
  .tla-checkbox.checked { border-color:#1E3A8A; background:#1E3A8A; }
  .tla-toggle-name { font-size:14px; font-weight:600; color:#0F172A; }
  .tla-toggle-desc { font-size:12px; color:#94A3B8; margin-top:1px; }

  /* footer */
  .tla-footer {
    padding:16px 28px; border-top:1px solid #F1F3F9;
    display:flex; align-items:center; justify-content:space-between;
    flex-shrink:0; background:#fff;
  }
  .tla-ready-badge {
    display:flex; align-items:center; gap:8px;
    background:#EFF6FF; border:1px solid #BFDBFE;
    padding:8px 14px; border-radius:999px;
    font-size:13px; font-weight:600; color:#1E40AF;
  }
  .tla-footer-btns { display:flex; gap:10px; }
  .tla-btn-close {
    padding:10px 22px; border-radius:10px;
    border:1.5px solid #E2E8F0; background:#fff;
    font-family:'DM Sans',sans-serif; font-size:14px; font-weight:600;
    color:#64748B; cursor:pointer; transition:all 0.15s;
  }
  .tla-btn-close:hover { background:#F8FAFC; border-color:#CBD5E1; color:#0F172A; }
  .tla-btn-assign {
    padding:10px 28px; border-radius:10px; border:none;
    background:#1E3A8A; color:#fff;
    font-family:'DM Sans',sans-serif; font-size:14px; font-weight:700;
    cursor:pointer; transition:all 0.15s; display:flex; align-items:center; gap:8px;
  }
  .tla-btn-assign:hover:not(:disabled) { background:#1E40AF; }
  .tla-btn-assign:disabled { opacity:0.55; cursor:not-allowed; }
  .tla-spinner-sm {
    width:15px; height:15px; border:2px solid rgba(255,255,255,0.3);
    border-top-color:#fff; border-radius:50%; animation:tla-spin 0.7s linear infinite;
  }
  .tla-empty { text-align:center; padding:20px; color:#94A3B8; font-size:13px; }
`;

/* ─── icons ──────────────────────────────────────────────────────────────── */
const IcoClose   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>;
const IcoSearch  = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>;
const IcoChevron = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"/></svg>;
const IcoCheck   = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>;
const IcoInfo    = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const IcoPeople  = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1E40AF" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IcoSend    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════════════════════════════════════ */
export const TeamLeaderAssignLeadsDialog = ({ open, onClose, refreshData }) => {
  const dispatch = useDispatch();

  const leadsallRef    = useRef([]);
  const [leadsCount,   setLeadsCount]   = useState(0);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [salesname,    setSalesname]    = useState('');
  const [selectedSale, setSelectedSale] = useState(null);
  const [selectedOption, setSelectedOption] = useState('option1');
  const [selectedStage,  setSelectedStage]  = useState(null);
  const [clearHistory,   setClearHistory]   = useState(false);
  const [sendloading,    setSendloading]    = useState(false);
  const [userlogin,      setUserlogin]      = useState(null);

  // Redux
  const resSalesData  = useSelector(s => s.AllSales.salesforteamleaders?.data || []);
  const resStagesData = useSelector(s => s.Allstages.stage?.data || []);

  // load user + leads on open
  useEffect(() => {
    if (!open) return;
    const u = localStorage.getItem('user');
    if (u) setUserlogin(JSON.parse(u));

    const stored = localStorage.getItem('selectedleedsbyteamleader');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        leadsallRef.current = parsed;
        setLeadsCount(parsed.length);
      } catch { leadsallRef.current = []; setLeadsCount(0); }
    } else { leadsallRef.current = []; setLeadsCount(0); }

    dispatch(GetAll_stage_Data());
  }, [open]);

  useEffect(() => {
    if (open && userlogin?.data?.email) {
      dispatch(Get_All_Sales_By_TeamLeader_Email_Action(userlogin.data.email));
    }
  }, [open, userlogin?.data?.email]);

  // filtered sales (exclude self)
  const filteredSales = React.useMemo(() => {
    const base = resSalesData.filter(s => s?.userlog?.name !== userlogin?.data?.name);
    const q = searchQuery.toLowerCase();
    return q ? base.filter(s => s.name?.toLowerCase().includes(q) || s.userlog?.email?.toLowerCase().includes(q)) : base;
  }, [resSalesData, userlogin, searchQuery]);

  const handleClose = () => {
    setSalesname(''); setSelectedSale(null);
    setSelectedOption('option1'); setSelectedStage(null);
    setClearHistory(false); setSearchQuery('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!salesname)             return Notify('error', 'Please select a sales person', 'error');
    if (!leadsallRef.current.length) return Notify('error', 'No leads selected', 'error');

    setSendloading(true);
    try {
      const stageValue = selectedOption === 'option2' && selectedStage ? selectedStage : undefined;
      for (const chunk of chunkArray(leadsallRef.current, 10)) {
        await Promise.all(chunk.map(leadId =>
          dispatch(AssignleadsinteamleaderAction(leadId, userlogin?.data?._id, salesname, clearHistory, stageValue))
        ));
      }
      Notify('success', `${leadsallRef.current.length} lead(s) assigned successfully`, 'success');
      localStorage.removeItem('selectedleedsbyteamleader');
      refreshData?.();
      handleClose();
    } catch {
      Notify('error', 'Failed to assign leads', 'error');
    } finally {
      setSendloading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      <style>{STYLES}</style>
      <div className="tla-overlay" onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
        <div className="tla-modal">

          {/* ── Header ── */}
          <div className="tla-header">
            <div className="tla-header-top">
              <div>
                <h2 className="tla-title">Assign Leads to Sales</h2>
                <div className="tla-subtitle">
                  <span className="tla-subtitle-dot" />
                  {leadsCount} Lead{leadsCount !== 1 ? 's' : ''} selected — Team Leader Panel
                </div>
              </div>
              <button className="tla-close" onClick={handleClose}><IcoClose /></button>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="tla-body">

            {/* Summary banner */}
            <div className="tla-summary">
              <div className="tla-summary-icon"><IcoPeople /></div>
              <div>
                <div className="tla-summary-count">{leadsCount}</div>
                <div className="tla-summary-label">Leads ready to assign</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div className="tla-summary-from-label">Assigning from</div>
                <div className="tla-summary-from">{userlogin?.data?.name || 'Team Leader'}</div>
              </div>
            </div>

            {/* Select Sales Person */}
            <p className="tla-section-label">Select Sales Person</p>

            <div className="tla-search-wrap">
              <span className="tla-search-icon"><IcoSearch /></span>
              <input
                className="tla-search-input"
                placeholder="Search sales by name or email…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <span className="tla-search-arrow"><IcoChevron /></span>
            </div>

            <div className="tla-sales-list">
              {filteredSales.length === 0 ? (
                <div className="tla-empty">
                  {resSalesData.length === 0 ? 'Loading sales team…' : 'No sales found'}
                </div>
              ) : filteredSales.map(sale => (
                <div
                  key={sale._id}
                  className={`tla-sales-item ${salesname === sale._id ? 'selected' : ''}`}
                  onClick={() => { setSalesname(sale._id); setSelectedSale(sale); }}
                >
                  <div
                    className="tla-avatar"
                    style={{ background: avatarBg(sale.name), color: avatarFg(sale.name) }}
                  >
                    {getInitials(sale.name)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="tla-sales-name">{sale.name}</div>
                    <div className="tla-sales-email">{sale.userlog?.email || sale.email || '—'}</div>
                  </div>
                  <div className={`tla-radio ${salesname === sale._id ? 'checked' : ''}`} />
                </div>
              ))}
            </div>

            {/* Bottom two-col */}
            <div className="tla-bottom-grid">

              {/* Stage Options */}
              <div>
                <p className="tla-section-label">Stage Options</p>
                <div className="tla-stage-option" onClick={() => setSelectedOption('option1')}>
                  <div className={`tla-stage-radio ${selectedOption === 'option1' ? 'checked' : ''}`} />
                  <div>
                    <div className="tla-stage-text-name">Same Stage</div>
                    <div className="tla-stage-text-desc">Keep current stage</div>
                  </div>
                </div>
                <div className="tla-stage-option" onClick={() => setSelectedOption('option2')}>
                  <div className={`tla-stage-radio ${selectedOption === 'option2' ? 'checked' : ''}`} />
                  <div>
                    <div className="tla-stage-text-name">Change Stage</div>
                    <div className="tla-stage-text-desc">Select new stage</div>
                  </div>
                </div>
                {selectedOption === 'option2' && (
                  <select
                    className="tla-stage-select"
                    value={selectedStage || ''}
                    onChange={e => setSelectedStage(e.target.value || null)}
                  >
                    <option value="">Select Stage</option>
                    {resStagesData
                      .filter(st => st.name?.toLowerCase() !== 'fresh')
                      .map(st => (
                        <option key={st._id} value={st._id}>
                          {st.name === 'No Stage' ? 'Fresh' : st.name}
                        </option>
                      ))}
                  </select>
                )}
              </div>

              {/* Advanced Settings */}
              <div>
                <p className="tla-section-label">Advanced Settings</p>
                <div className="tla-toggle-row" onClick={() => setClearHistory(v => !v)}>
                  <div className={`tla-checkbox ${clearHistory ? 'checked' : ''}`}>
                    {clearHistory && <IcoCheck />}
                  </div>
                  <div>
                    <div className="tla-toggle-name">Clear previous comments</div>
                    <div className="tla-toggle-desc">for this sales person</div>
                  </div>
                </div>
              </div>
            </div>

          </div>{/* /body */}

          {/* ── Footer ── */}
          <div className="tla-footer">
            <div className="tla-ready-badge">
              <IcoInfo />
              READY TO ASSIGN {leadsCount} LEAD{leadsCount !== 1 ? 'S' : ''}
            </div>
            <div className="tla-footer-btns">
              <button className="tla-btn-close" onClick={handleClose}>Close</button>
              <button
                className="tla-btn-assign"
                onClick={handleSubmit}
                disabled={sendloading || !salesname || !leadsCount}
              >
                {sendloading
                  ? <><div className="tla-spinner-sm" />Assigning…</>
                  : <><IcoSend />Assign to Sales</>}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>,
    document.body
  );
};
